import { useMemo, useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import appConfig from 'app-config';
import { DTO_PROFILE_CARD_NICKNAME_REVIEW_STATE_TYPES as NicknameReviewStatus } from 'src/apis/dto/DTOProfileCard';
import { NICKNAME_MAX_LENGTH } from 'src/components/AzarOnly/Login/constants';
import Button from 'src/components/Button';
import { MobileModalType, ModalContent as ModalContentBase, ModalFooter, ModalHeader, ModalHeaderTitle } from 'src/components/ModalLayout';
import ModalLayoutBase from 'src/components/ModalLayout';
import Textarea from 'src/components/Textarea';
import { useMyProfile } from 'src/hooks/useMyProfile';
import useNicknameChange from 'src/hooks/useNicknameChange';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
const ModalLayout = styled(ModalLayoutBase)`
  max-width: 400px;
`;
const ModalContent = styled(ModalContentBase)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Description = styled.p`
  color: ${({
  theme
}) => theme.color.content9};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  white-space: pre-wrap;
`;
const VividiOnlyDescription = styled(Description)`
  ${appConfig.serviceCode !== 'vividi' && 'display: none;'}
`;
const EditNicknameModal = () => {
  const {
    t
  } = useTranslation();
  const profile = useMyProfile();
  const [nickname, setNickname] = useState(profile?.nickname || '');
  const closeModal = useSetAtom(closeModalAtom);
  const {
    isPending,
    updateNickname,
    isSuccess
  } = useNicknameChange();
  const handleClose = useCallback(() => {
    closeModal(ModalType.EDIT_NICKNAME);
  }, [closeModal]);
  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess, handleClose]);
  const handleSave = async () => {
    await updateNickname(nickname);
  };
  const handleChangeNickname = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNickname(e.target.value);
  };
  const isSaveButtonDisabled = useMemo(() => {
    if (isPending) {
      return true;
    }
    if (nickname === profile?.nickname) {
      return true;
    }
    if (nickname.length < 1 || nickname.length > NICKNAME_MAX_LENGTH) {
      return true;
    }
    return false;
  }, [isPending, nickname, profile?.nickname]);
  const nicknameDescription = useMemo(() => {
    if (profile?.nicknameReviewState === NicknameReviewStatus.PENDING) {
      return t('edit_nickname_pending');
    }
    return t('edit_nickname_desc');
  }, [profile?.nicknameReviewState, t]);
  return <ModalLayout navButtonType={ModalNavButtonType.BACK} mobileType={MobileModalType.SCREEN_MODAL} mobileNavButtonType={ModalNavButtonType.BACK} isDim onBack={handleClose} data-sentry-element="ModalLayout" data-sentry-component="EditNicknameModal" data-sentry-source-file="index.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx">
        <ModalHeaderTitle mobileType={MobileModalType.SCREEN_MODAL} buttonType={ModalNavButtonType.BACK} data-sentry-element="ModalHeaderTitle" data-sentry-source-file="index.tsx">
          {t('edit_nickname_title')}
        </ModalHeaderTitle>
      </ModalHeader>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <VividiOnlyDescription data-sentry-element="VividiOnlyDescription" data-sentry-source-file="index.tsx">{t('VIVIDI_PROFILE_CHANGENICK_DISABLED')}</VividiOnlyDescription>
        <Textarea placeholder={t('edit_nickname_placeholder')} onChange={handleChangeNickname} value={nickname} maxLength={NICKNAME_MAX_LENGTH} hasCount countDisplayFormat='fraction' data-sentry-element="Textarea" data-sentry-source-file="index.tsx" />
        <Description data-sentry-element="Description" data-sentry-source-file="index.tsx">{nicknameDescription}</Description>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
        <Button id='edit-nickname-modal-save-button' color='primary' onClick={handleSave} disabled={isSaveButtonDisabled} loading={isPending} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {t('SAVE')}
        </Button>
      </ModalFooter>
    </ModalLayout>;
};
export default EditNicknameModal;